import {match} from 'ts-pattern';

import {DashboardTileSize} from '../types/DashboardTileSize';

const EXACT_FIT_TOLERANCE = 0.5;
const STACKED_BASELINE_ROWS = 3;
const SIDE_BY_SIDE_BASELINE_ROWS = 2;

export interface VehicleWarehouseMotRowCounts {
  expired: number;
  expiring: number;
}

export interface VehicleWarehouseMotLayout {
  mode: 'aggregate' | 'detail';
  rows: VehicleWarehouseMotRowCounts;
  minimumHeight: number | null;
  contentHeight: number | null;
}

interface GetVehicleWarehouseMotLayoutOptions {
  size: DashboardTileSize;
  availableHeight: number | null;
  aggregateHeight: number | null;
  stackedGap: number | null;
  expiredSectionHeights: readonly (number | null)[];
  expiringSectionHeights: readonly (number | null)[];
  expiredRowCount: number;
  expiringRowCount: number;
  canShowDetail: boolean;
}

const getMeasuredHeight = (heights: readonly (number | null)[], rowCount: number) =>
  heights[rowCount] ?? null;

const fits = (height: number | null, availableHeight: number | null) =>
  height !== null && availableHeight !== null && height <= availableHeight + EXACT_FIT_TOLERANCE;

const getStackedHeight = (
  rows: VehicleWarehouseMotRowCounts,
  options: GetVehicleWarehouseMotLayoutOptions
) => {
  const expiredHeight = getMeasuredHeight(options.expiredSectionHeights, rows.expired);
  const expiringHeight = getMeasuredHeight(options.expiringSectionHeights, rows.expiring);

  if (expiredHeight === null || expiringHeight === null || options.stackedGap === null) {
    return null;
  }

  return expiredHeight + options.stackedGap + expiringHeight;
};

const getNextBalancedRows = (
  rows: VehicleWarehouseMotRowCounts,
  maximumRows: VehicleWarehouseMotRowCounts
): VehicleWarehouseMotRowCounts | null => {
  const canAddExpired = rows.expired < maximumRows.expired;
  const canAddExpiring = rows.expiring < maximumRows.expiring;

  return match({canAddExpired, canAddExpiring, isExpiredBehind: rows.expired <= rows.expiring})
    .with({canAddExpired: false, canAddExpiring: false}, () => null)
    .with({canAddExpired: true, canAddExpiring: false}, () => ({
      ...rows,
      expired: rows.expired + 1,
    }))
    .with({canAddExpired: true, isExpiredBehind: true}, () => ({
      ...rows,
      expired: rows.expired + 1,
    }))
    .otherwise(() => ({...rows, expiring: rows.expiring + 1}));
};

const fitStackedRows = (
  initialRows: VehicleWarehouseMotRowCounts,
  maximumRows: VehicleWarehouseMotRowCounts,
  options: GetVehicleWarehouseMotLayoutOptions
) => {
  let rows = initialRows;
  let nextRows = getNextBalancedRows(rows, maximumRows);

  while (nextRows !== null && fits(getStackedHeight(nextRows, options), options.availableHeight)) {
    rows = nextRows;
    nextRows = getNextBalancedRows(rows, maximumRows);
  }

  return rows;
};

const fitSectionRows = (
  baselineRows: number,
  maximumRows: number,
  heights: readonly (number | null)[],
  availableHeight: number | null
) => {
  let rows = baselineRows;

  while (rows < maximumRows && fits(getMeasuredHeight(heights, rows + 1), availableHeight)) {
    rows += 1;
  }

  return rows;
};

export function getVehicleWarehouseMotBaselineLayout(
  size: DashboardTileSize,
  expiredRowCount: number,
  expiringRowCount: number
): VehicleWarehouseMotLayout {
  const getRows = (limit: number) => ({
    expired: Math.min(limit, expiredRowCount),
    expiring: Math.min(limit, expiringRowCount),
  });

  return match(size)
    .with('tall', () => ({
      mode: 'detail' as const,
      rows: getRows(STACKED_BASELINE_ROWS),
      minimumHeight: null,
      contentHeight: null,
    }))
    .with('wide', () => ({
      mode: 'detail' as const,
      rows: getRows(SIDE_BY_SIDE_BASELINE_ROWS),
      minimumHeight: null,
      contentHeight: null,
    }))
    .otherwise(() => ({
      mode: 'aggregate' as const,
      rows: {expired: 0, expiring: 0},
      minimumHeight: null,
      contentHeight: null,
    }));
}

export function getVehicleWarehouseMotLayout(
  options: GetVehicleWarehouseMotLayoutOptions
): VehicleWarehouseMotLayout {
  const maximumRows = {
    expired: Math.min(options.expiredRowCount, options.expiredSectionHeights.length - 1),
    expiring: Math.min(options.expiringRowCount, options.expiringSectionHeights.length - 1),
  };

  return match(options.size)
    .with('half', () => {
      const minimumDetailRows = {
        expired: Math.min(1, maximumRows.expired),
        expiring: Math.min(1, maximumRows.expiring),
      };
      const minimumDetailHeight = getStackedHeight(minimumDetailRows, options);

      if (!options.canShowDetail || !fits(minimumDetailHeight, options.availableHeight)) {
        return {
          mode: 'aggregate' as const,
          rows: {expired: 0, expiring: 0},
          minimumHeight: options.aggregateHeight,
          contentHeight: options.aggregateHeight,
        };
      }

      const rows = fitStackedRows(minimumDetailRows, maximumRows, options);

      return {
        mode: 'detail' as const,
        rows,
        minimumHeight: options.aggregateHeight,
        contentHeight: getStackedHeight(rows, options),
      };
    })
    .with('tall', () => {
      const baselineRows = {
        expired: Math.min(STACKED_BASELINE_ROWS, maximumRows.expired),
        expiring: Math.min(STACKED_BASELINE_ROWS, maximumRows.expiring),
      };

      const rows = fitStackedRows(baselineRows, maximumRows, options);

      return {
        mode: 'detail' as const,
        rows,
        minimumHeight: getStackedHeight(baselineRows, options),
        contentHeight: getStackedHeight(rows, options),
      };
    })
    .with('wide', () => {
      const baselineRows = {
        expired: Math.min(SIDE_BY_SIDE_BASELINE_ROWS, maximumRows.expired),
        expiring: Math.min(SIDE_BY_SIDE_BASELINE_ROWS, maximumRows.expiring),
      };
      const expiredBaselineHeight = getMeasuredHeight(
        options.expiredSectionHeights,
        baselineRows.expired
      );
      const expiringBaselineHeight = getMeasuredHeight(
        options.expiringSectionHeights,
        baselineRows.expiring
      );

      const rows = {
        expired: fitSectionRows(
          baselineRows.expired,
          maximumRows.expired,
          options.expiredSectionHeights,
          options.availableHeight
        ),
        expiring: fitSectionRows(
          baselineRows.expiring,
          maximumRows.expiring,
          options.expiringSectionHeights,
          options.availableHeight
        ),
      };
      const expiredContentHeight = getMeasuredHeight(options.expiredSectionHeights, rows.expired);
      const expiringContentHeight = getMeasuredHeight(
        options.expiringSectionHeights,
        rows.expiring
      );

      return {
        mode: 'detail' as const,
        rows,
        minimumHeight:
          expiredBaselineHeight === null || expiringBaselineHeight === null
            ? null
            : Math.max(expiredBaselineHeight, expiringBaselineHeight),
        contentHeight:
          expiredContentHeight === null || expiringContentHeight === null
            ? null
            : Math.max(expiredContentHeight, expiringContentHeight),
      };
    })
    .otherwise(() => ({
      mode: 'aggregate',
      rows: {expired: 0, expiring: 0},
      minimumHeight: options.aggregateHeight,
      contentHeight: options.aggregateHeight,
    }));
}
